/**
 * StoryLabelBadges — visual pills for story labels (Documentary /
 * Storylo Original / Charity Supported / …). Shared between the public
 * gallery, library cards and story pages so a story's badge set looks
 * identical everywhere.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level cache so every card doesn't re-fetch.
let _cachedLabels = null;
let _fetchInFlight = null;

async function _loadLabels() {
  if (_cachedLabels) return _cachedLabels;
  if (_fetchInFlight) return _fetchInFlight;
  _fetchInFlight = axios
    .get(`${API}/taxonomy`)
    .then(({ data }) => {
      const map = {};
      for (const lbl of data.story_labels || []) map[lbl.slug] = lbl;
      _cachedLabels = map;
      return map;
    })
    .catch(() => ({}))
    .finally(() => {
      _fetchInFlight = null;
    });
  return _fetchInFlight;
}

export default function StoryLabelBadges({ labels = [], size = "sm", className = "" }) {
  const [meta, setMeta] = useState(_cachedLabels || {});
  useEffect(() => {
    if (!_cachedLabels) _loadLabels().then(setMeta);
  }, []);
  if (!labels || labels.length === 0) return null;
  const sizeCls =
    size === "xs"
      ? "text-[9px] px-1.5 py-0.5"
      : size === "md"
      ? "text-[11px] px-2.5 py-1"
      : "text-[10px] px-2 py-0.5";
  return (
    <div className={`flex flex-wrap gap-1 ${className}`} data-testid="story-labels">
      {labels.map((slug) => {
        const m = meta[slug];
        const label = m?.label || slug.replace(/_/g, " ");
        const color = m?.color || "#94a3b8";
        return (
          <span
            key={slug}
            className={`inline-flex items-center uppercase tracking-[0.18em] rounded-full font-medium ${sizeCls}`}
            style={{
              background: `${color}22`,
              color,
              border: `1px solid ${color}55`,
            }}
            data-testid={`story-label-${slug}`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}
